import React, {CSSProperties, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import iconLeft from '../../../assets/images/icons/settings/left.png';
import iconUser from '../../../assets/images/icons/settings/user.png';
import iconSearch from '../../../assets/images/icons/settings/search.png';
import iconCross from '../../../assets/images/icons/settings/cross.png';
import './index.scss';
import {onClickHandler} from '../../../utils/onclick';
import {Link} from 'react-router-dom';
import {useOrientation} from '../../../hooks/orientation';
import {KeyboardTuple, useKeyboard} from '../../../hooks/keyboard';
import {useDispatch} from 'react-redux';
import {changeBusyAction} from '../../../store/ui/actions';
import {useIsMounted} from '../../../hooks/is-mounted';
import {isPromise} from '../../../utils/promise';
import {SettingInputComponent} from '../SettingInputComponent';
import {SearchContext} from '../../../hooks/search-term';

export type SettingsPageContainerProps = {
    className?: string;
    children?: ReactNode;
    backUrl?: string;
    buttons: ButtonProps[];
    currentInput?: KeyboardTuple;
    currentNumpadInput?: KeyboardTuple;
    currentNumpadExtendedInput?: KeyboardTuple;
    title?: string;
    doNotBlockUI?: boolean;
    style?: CSSProperties;
    searchable?: boolean;
}

export type ButtonProps = {
    className?: string;
    onClick?: () => any;
    url?: string;
    text?: string;
    renderContent?: () => ReactNode;
    hidden?: boolean;
    withState?: () => any;
    component?: ReactNode;
    doNotBlockUI?: boolean;
};

const ButtonComponent = ({onClick, text, className, url, withState, renderContent, doNotBlockUI}: ButtonProps) => {
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const [isBusy, setIsBusy] = useState(false);
    let Element = (props) => <div {...props}/>;
    if (!onClick) {
        // @ts-ignore
        Element = Link;
    }

    const internalOnClick = useCallback(() => {
        if (isBusy) {
            return;
        }

        if (onClick) {
            const output = onClick();
            if (isPromise(output)) {
                if (isMounted() === true) {
                    setIsBusy(true);
                }
                if (doNotBlockUI !== true) {
                    dispatch(changeBusyAction(true));
                }
                (output as Promise<any>)
                    .finally(() => {
                        if (isMounted() === true) {
                            setIsBusy(false);
                        }
                        if (doNotBlockUI !== true) {
                            dispatch(changeBusyAction(false));
                        }
                    });
            }
        }
    }, [isBusy, onClick, doNotBlockUI, dispatch, isMounted]);

    return (
        <Element
            key={`button_${text}`}
            className={(isBusy ? 'busy ' : '') + 'button ' + className}
            {
                ...onClick ? onClickHandler(internalOnClick) : {
                    to: {
                        pathname: url,
                        state: withState ? withState() : undefined,
                    },
                }
            }>
            {
                renderIf(renderContent)(() =>
                        <>
                            {
                                renderContent()
                            }
                        </>,
                    () =>
                        <span className="time">
                            {text}
                        </span>,
                )
            }
        </Element>
    );
};

export const SettingsPageContainer = memo(
    function SettingsPage(props: SettingsPageContainerProps) {
        const {
            className, children, backUrl,
            buttons, currentInput, currentNumpadInput,
            currentNumpadExtendedInput, title, style,
            searchable,
        } = props;
        const orientation = useOrientation();
        const childrenRef = useRef<HTMLDivElement>();
        const [setActiveInput, renderKeyboard] = useKeyboard();

        const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
        const [searchTerm, setSearchTerm] = useState<string>('');

        const selectedRef = useMemo(() => {
            if (currentInput) {
                return currentInput[0];
            }

            if (currentNumpadInput) {
                return currentNumpadInput[0];
            }

            if (currentNumpadExtendedInput) {
                return currentNumpadExtendedInput[0];
            }

            return null;
        }, [currentInput, currentNumpadExtendedInput, currentNumpadInput]);

        const onBlur = useCallback(() => {
            setActiveInput(null);
        }, [setActiveInput]);

        const onCrossClick = useCallback(() => {
            if (searchTerm === '') {
                setIsSearchActive(false);
                return;
            }
            setActiveInput(null);
            setSearchTerm('');
        }, [searchTerm, setActiveInput]);

        const keyboardVisible = Boolean(selectedRef);

        useEffect(() => {
            if (selectedRef) {
                childrenRef.current.scrollTo(0, (selectedRef.current.offsetParent as HTMLElement).offsetTop - 60);
            }
        }, [selectedRef]);

        useEffect(() => {
            if (searchable) {
                return;
            }

            setIsSearchActive(false);
            setActiveInput(null);
            setSearchTerm('');

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [searchable]);

        return (
            <div
                className={'settings-page-container ' + orientation + (className ? ' ' + className : '') + (keyboardVisible ? ' keyboard-visible' : '')}
                style={style}>
                <div className="content-header">
                    <div className="actions">
                        <Link className="back-button" to={backUrl}>
                            <>
                                <img className="small" src={iconLeft} alt="back"/>
                                <img src={iconUser} alt="users"/>
                            </>
                        </Link>
                        {renderIf(searchable)(() =>
                            <div className="search-container">
                                <img
                                    src={iconSearch}
                                    className="search-icon"
                                    alt="search"
                                    {...onClickHandler(() => setIsSearchActive(true))}
                                />
                                <div className={'input-container ' + (isSearchActive ? 'visible' : 'hidden')}>
                                    <SettingInputComponent
                                        placeholder=""
                                        label=""
                                        value={searchTerm}
                                        setValue={setSearchTerm}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <img
                                        src={iconCross}
                                        className="cross-icon"
                                        alt="cross"
                                        {...onClickHandler(onCrossClick)}
                                    />
                                </div>
                            </div>,
                        )}
                        <div className="title-and-button-container">
                            <div className="title-container">
                                {
                                    renderIf(orientation !== 'vertical')(() =>
                                        <span className="title-text">{title || ''}</span>,
                                    )
                                }
                            </div>
                            <div className="right-buttons">
                                {
                                    buttons
                                        .filter(button => button != null && !button.hidden)
                                        .map((button, index) => {
                                                if (button.component != null) {
                                                    return button.component;
                                                }

                                                return (
                                                    <ButtonComponent {...button}
                                                                     key={`key_${index}`}/>
                                                );
                                            },
                                        )
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className="children" ref={childrenRef}>
                    <SearchContext.Provider value={searchTerm}>
                        {children}
                    </SearchContext.Provider>
                </div>
                {
                    renderKeyboard()
                }
            </div>
        );
    },
);
